import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import {
  AuthorizationParams,
  IRedocOauth2Config,
  RedocOAuthEvent,
  RedocOAuthInfoEvent,
  RedocOAuthLogger,
  RedocOAuthStorage,
  RedocOauth2ClientConnect,
  RedocOauth2ClientConnectOptionCallback,
} from '@redocco/oauth2-redoc-connect';
import { Observable, Subject, Subscription, delay, filter, of } from 'rxjs';
import { REDOC_OAUTH2_CONFIG } from './redoc-connect.token';

@Injectable()
export class RedocConnectService {
  clientConnect!: RedocOauth2ClientConnect;
  options: Partial<IRedocOauth2Config> = inject(REDOC_OAUTH2_CONFIG);
  logger: RedocOAuthLogger = inject(RedocOAuthLogger);
  storage: RedocOAuthStorage = inject(RedocOAuthStorage);
  ngZone: NgZone = inject(NgZone);
  http: HttpClient = inject(HttpClient);
  document = inject(DOCUMENT);
  tokenReceivedSubscription?: Subscription;
  accessTokenTimeoutSubscription?: Subscription;
  idTokenTimeoutSubscription?: Subscription;
  automaticRefreshSubscription?: Subscription;
  callbackOption: Partial<RedocOauth2ClientConnectOptionCallback> = {
    onEvent: e => {
      this.pushEvent(e);
    },
    onConfigChanged: () => {
      this.setupRefreshTimer();
    },
  };
  protected eventsSubject: Subject<RedocOAuthEvent> = new Subject<RedocOAuthEvent>();
  events: Observable<RedocOAuthEvent> = this.eventsSubject.asObservable();
  constructor() {
    this.clientConnect = new RedocOauth2ClientConnect(this.storage, this.document, this.logger, this.callbackOption);
    this.clientConnect.configure(this.options);
    // this.setupRefreshTimer();
  }
  configure(config: Partial<IRedocOauth2Config>) {
    this.clientConnect.configure(config);
  }
  loadDiscoveryDocumentAndTryLogin() {
    return this.clientConnect.loadDiscoveryDocumentAndTryLogin();
  }
  tryLogin() {
    return this.clientConnect.tryLogin();
  }
  loginWithRedirect(additionalState?: string, params: Partial<AuthorizationParams> = {}): void {
    const config = this.clientConnect.config;
    const url = this.createUrl(config.loginUrl, { client_id: config.clientId, redirect_uri: additionalState, ...params })
    this.clientConnect.openUri(url)
    // this.clientConnect.initLoginFlow(additionalState, params);
  }
  createUrl(
    baseUrl: string,
    params: Record<string, any>
  ): string {
    // const seperationChar = baseUrl.indexOf('?') > -1 ? '&' : '?';
    const url = new URL(baseUrl);
    const querySearch = new URLSearchParams(params);
    url.search = querySearch.toString();
    // const paramString = Object.entries(params)
    //   .filter(([key, value]) => !!value)
    //   .map(([key, value]) => {
    //     return `${key}=${encodeURIComponent(value)}`;
    //   })
    //   .join('&');
    return url.href;
  }
  loginWithPopup(params: Partial<AuthorizationParams> = {}): void {
    this.clientConnect.loginWithPopup(params);
  }
  logOut(): void {
    this.clientConnect.logOut();
  }
  get state() {
    return this.clientConnect.state;
  }
  getRefreshToken() {
    return this.clientConnect.getRefreshToken();
  }
  getAccessToken() {
    return this.clientConnect.getAccessToken();
  }
  hasValidAccessToken(): boolean {
    return this.clientConnect.hasValidAccessToken();
  }
  setupSessionCheck(): void {
    // this.events
    //   .pipe(filter((e) => e.type === 'token_received'))
    //   .subscribe((e) => {
    //     this.initSessionCheck();
    //   });
  }
  setupRefreshTimer(): void {
    if (typeof window === 'undefined') {
      this.clientConnect.debug('timer not supported on this plattform');
      return;
    }
    if (this.clientConnect.hasValidIdToken() || this.clientConnect.hasValidAccessToken()) {
      this.clearAccessTokenTimer();
      this.clearIdTokenTimer();
      this.setupExpirationTimers();
    }

    if (this.tokenReceivedSubscription) {
      this.tokenReceivedSubscription.unsubscribe();
    }

    this.tokenReceivedSubscription = this.events.pipe(filter(e => e.type === 'token_received')).subscribe(_ => {
      this.clearAccessTokenTimer();
      this.clearIdTokenTimer();
      this.setupExpirationTimers();
    });
  }
  clearAccessTokenTimer(): void {
    if (this.accessTokenTimeoutSubscription) {
      this.accessTokenTimeoutSubscription.unsubscribe();
    }
  }
  clearIdTokenTimer(): void {
    if (this.idTokenTimeoutSubscription) {
      this.idTokenTimeoutSubscription.unsubscribe();
    }
  }
  clearAutomaticRefreshTimer(): void {
    if (this.automaticRefreshSubscription) {
      this.automaticRefreshSubscription.unsubscribe();
    }
  }
  setupExpirationTimers(): void {
    if (this.clientConnect.hasValidAccessToken()) {
      this.setupAccessTokenTimer();
    }

    if (!this.clientConnect.config.disableIdTokenTimer && this.clientConnect.hasValidIdToken()) {
      this.setupIdTokenTimer();
    }
  }
  setupAccessTokenTimer(): void {
    const timeout = this.clientConnect.calcTokenTimeout('access_token');

    this.ngZone.runOutsideAngular(() => {
      this.accessTokenTimeoutSubscription = of(new RedocOAuthInfoEvent('token_expires', 'access_token'))
        .pipe(delay(timeout))
        .subscribe(e => {
          this.ngZone.run(() => {
            this.eventsSubject.next(e);
          });
        });
    });
  }
  setupIdTokenTimer(): void {
    const timeout = this.clientConnect.calcTokenTimeout('id_token');

    this.ngZone.runOutsideAngular(() => {
      this.idTokenTimeoutSubscription = of(new RedocOAuthInfoEvent('token_expires', 'id_token'))
        .pipe(delay(timeout))
        .subscribe(e => {
          this.ngZone.run(() => {
            this.pushEvent(e);
          });
        });
    });
  }
  public stopAutomaticRefresh() {
    this.clearAccessTokenTimer();
    this.clearIdTokenTimer();
    this.clearAutomaticRefreshTimer();
  }

  pushEvent(event: RedocOAuthEvent): void {
    this.eventsSubject.next(event);
  }
}
